import { uploadPhotosWithRealtimeUpdate } from '../../../services/DeliveryDataService';
import { PhotoType, PositionType } from '../services/PhotoService';

/**
 * 照片上传管理器 - 支持实时更新和状态同步
 */
export class PhotoUploadManager {
    private showMultiPhotoSelector: boolean;
    private trackingNumber: string;

    // 状态更新回调
    private setPhotosArray?: React.Dispatch<React.SetStateAction<PhotoType[]>>;
    private setPhotos?: React.Dispatch<React.SetStateAction<Record<PositionType, PhotoType | undefined>>>;

    // 本地副本，用于异常时保存
    private latestPhotosArray: PhotoType[] = [];
    private latestPhotos: Record<PositionType, PhotoType | undefined> = {} as Record<PositionType, PhotoType | undefined>;

    constructor(
        showMultiPhotoSelector: boolean,
        trackingNumber: string,
        setPhotosArray?: React.Dispatch<React.SetStateAction<PhotoType[]>>,
        setPhotos?: React.Dispatch<React.SetStateAction<Record<PositionType, PhotoType | undefined>>>
    ) {
        this.showMultiPhotoSelector = showMultiPhotoSelector;
        this.trackingNumber = trackingNumber;
        this.setPhotosArray = setPhotosArray;
        this.setPhotos = setPhotos;
    }

    /**
     * 初始化本地副本
     */
    initializeLocalCopies(
        photosArray: PhotoType[],
        photos: Record<PositionType, PhotoType | undefined>
    ): void {
        this.latestPhotosArray = [...photosArray];
        this.latestPhotos = {...photos};
    }

    /**
     * 获取需要上传的照片列表
     */
    getPhotosToUpload(
        photosArray: PhotoType[],
        photos: Record<PositionType, PhotoType | undefined>
    ): PhotoType[] {
        return this.showMultiPhotoSelector
            ? photosArray.filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined)
            : Object.values(photos).filter((item): item is PhotoType => item?.objectKey !== undefined && item?.url !== undefined);
    }

    /**
     * 单张照片上传成功的回调处理
     */
    private handlePhotoUploaded = (index: number, objectKey: string): void => {

        if (this.showMultiPhotoSelector) {
            this.updatePhotosArrayState(index, objectKey);
            this.updatePhotosArrayLocal(index, objectKey);
        } else {
            this.updatePhotosState(index, objectKey);
            this.updatePhotosLocal(index, objectKey);
        }
    };

    /**
     * 更新新版本（数组）的React状态
     */
    private updatePhotosArrayState(index: number, objectKey: string): void {
        if (!this.setPhotosArray) return;

        this.setPhotosArray(prevArray => {
            const newArray = [...prevArray];
            let actualIndex = 0;

            // 找到实际需要更新的索引
            for (let i = 0; i < newArray.length; i++) {
                if (newArray[i]?.objectKey !== undefined && newArray[i]?.url !== undefined) {
                    if (actualIndex === index) {
                        newArray[i] = { ...newArray[i], objectKey };
                        break;
                    }
                    actualIndex++;
                }
            }
            return newArray;
        });
    }

    /**
     * 更新新版本（数组）的本地副本
     */
    private updatePhotosArrayLocal(index: number, objectKey: string): void {
        let actualIndex = 0;
        for (let i = 0; i < this.latestPhotosArray.length; i++) {
            if (this.latestPhotosArray[i]?.objectKey !== undefined && this.latestPhotosArray[i]?.url !== undefined) {
                if (actualIndex === index) {
                    this.latestPhotosArray[i] = { ...this.latestPhotosArray[i], objectKey };
                    break;
                }
                actualIndex++;
            }
        }
    }

    /**
     * 更新老版本（对象）的React状态
     */
    private updatePhotosState(index: number, objectKey: string): void {
        if (!this.setPhotos) return;

        this.setPhotos(prevPhotos => {
            const validPhotos = Object.entries(prevPhotos).filter(([_, photo]) =>
                photo?.objectKey !== undefined && photo?.url !== undefined
            );

            if (index < validPhotos.length) {
                const [positionType] = validPhotos[index];
                const updatedPhotos = {
                    ...prevPhotos,
                    [positionType]: { ...prevPhotos[positionType as PositionType], objectKey }
                };

                return updatedPhotos;
            }
            return prevPhotos;
        });
    }

    /**
     * 更新老版本（对象）的本地副本
     */
    private updatePhotosLocal(index: number, objectKey: string): void {
        const validPhotos = Object.entries(this.latestPhotos).filter(([_, photo]) =>
            photo?.objectKey !== undefined && photo?.url !== undefined
        );

        if (index < validPhotos.length) {
            const [positionType] = validPhotos[index];
            this.latestPhotos = {
                ...this.latestPhotos,
                [positionType]: { ...this.latestPhotos[positionType as PositionType], objectKey }
            };
        }
    }

    /**
     * 执行照片上传
     */
    async uploadPhotos(photosToUpload: PhotoType[]): Promise<string[]> {

        const photoKeys = await uploadPhotosWithRealtimeUpdate(
            photosToUpload,
            this.trackingNumber,
            this.handlePhotoUploaded
        );

        return photoKeys;
    }

    /**
     * 获取最新的照片数据（用于异常时保存到本地缓存）
     */
    getLatestPhotosData(): {
        photosArray?: PhotoType[];
        photos?: Record<PositionType, PhotoType | undefined>;
    } {
        return {
            photosArray: this.showMultiPhotoSelector ? this.latestPhotosArray : undefined,
            photos: !this.showMultiPhotoSelector ? this.latestPhotos : undefined
        };
    }

}
